import React, { useState, useEffect } from "react";
import { fetchValidationDashboard, fetchCrossStrategySummary } from "../../../lib/researchApi";
import GovernanceBadge from "../shared/GovernanceBadge";
import OutcomeConfidencePanel from "../shared/OutcomeConfidencePanel";

const STRATEGY_LABELS = {
    wheel:           "Wheel",
    earnings:        "Earnings",
    tactical_spread: "Tactical Spread",
    regime_spread:   "Regime Spread",
};

function ScoreBar({ value, thresholdWarn = 0.4, thresholdGood = 0.65, invert = false }) {
    if (value == null) return <span style={{ color: "var(--savvy-dim)" }}>—</span>;
    const pct = invert ? 1 - Math.min(value, 1) : Math.min(value, 1);
    const color = invert
        ? (value > thresholdWarn ? "#f87171" : value > 0.1 ? "#fbbf24" : "#4ade80")
        : (value >= thresholdGood ? "#4ade80" : value >= thresholdWarn ? "#fbbf24" : "#f87171");
    return (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ flex: 1, height: 4, background: "rgba(255,255,255,0.08)", borderRadius: 2 }}>
                <div style={{ height: "100%", width: `${pct * 100}%`, background: color, borderRadius: 2 }} />
            </div>
            <span style={{ fontSize: "12px", fontWeight: 600, color, minWidth: 40, textAlign: "right" }}>
                {(value * 100).toFixed(1)}%
            </span>
        </div>
    );
}

function RankingsTable({ title, rows, valueKey, label, invert, thresholdWarn, thresholdGood }) {
    return (
        <div style={{ background: "var(--savvy-panel)", border: "1px solid var(--savvy-border)", borderRadius: 10, padding: "16px" }}>
            <div style={{ fontSize: "11px", fontWeight: 600, color: "var(--savvy-muted)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 14 }}>
                {title}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {rows.map(([strategy, data], rank) => (
                    <div key={strategy}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <span style={{ fontSize: "11px", color: "var(--savvy-dim)", fontVariantNumeric: "tabular-nums", minWidth: 14 }}>
                                    {rank + 1}.
                                </span>
                                <span style={{ fontSize: "12px", color: "var(--savvy-text)", fontWeight: 500 }}>
                                    {STRATEGY_LABELS[strategy] || strategy}
                                </span>
                                <GovernanceBadge state={data.latest_governance_state} />
                            </div>
                        </div>
                        <ScoreBar
                            value={data[valueKey]}
                            invert={invert}
                            thresholdWarn={thresholdWarn}
                            thresholdGood={thresholdGood}
                        />
                        <div style={{ marginTop: 6 }}>
                            <OutcomeConfidencePanel
                                outcomeClassification={data.outcome_classification}
                                evidenceConfidence={data.evidence_confidence}
                                compact
                                showUnavailable
                            />
                        </div>
                    </div>
                ))}
                {rows.length === 0 && <div style={{ fontSize: "12px", color: "var(--savvy-dim)" }}>No data</div>}
            </div>
        </div>
    );
}

export default function RankingsSection() {
    const [dashData, setDashData] = useState(null);
    const [summaryData, setSummaryData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        setLoading(true);
        Promise.all([fetchValidationDashboard(), fetchCrossStrategySummary()])
            .then(([dash, summary]) => {
                setDashData(dash.strategies || {});
                setSummaryData(summary.strategy_summaries || summary.strategies || {});
            })
            .catch((e) => setError(e?.response?.data?.error || "Failed to load rankings"))
            .finally(() => setLoading(false));
    }, []);

    if (loading) return <div style={{ color: "var(--savvy-muted)", padding: 24, textAlign: "center", fontSize: "13px" }}>Loading rankings…</div>;
    if (error) return <div style={{ color: "#f87171", padding: 16, fontSize: "13px" }}>Error: {error}</div>;

    const strategies = Object.keys(dashData || {});

    function mergedRow(s) {
        return {
            ...dashData[s],
            ...summaryData[s],
            latest_governance_state: dashData[s]?.latest_governance_state,
        };
    }

    // Stability rankings: sort by avg_stability_score desc
    const stabilityRanked = [...strategies]
        .map(s => [s, mergedRow(s)])
        .filter(([, d]) => d.avg_stability_score != null)
        .sort((a, b) => (b[1].avg_stability_score || 0) - (a[1].avg_stability_score || 0));

    // Operational confidence: sort by operational_health_score desc
    const opConfRanked = [...strategies]
        .map(s => [s, mergedRow(s)])
        .filter(([, d]) => d.operational_health_score != null)
        .sort((a, b) => (b[1].operational_health_score || 0) - (a[1].operational_health_score || 0));

    // Deployment readiness: sort by deployment_readiness_score desc
    const deployRanked = [...strategies]
        .map(s => [s, mergedRow(s)])
        .filter(([, d]) => d.deployment_readiness_score != null)
        .sort((a, b) => (b[1].deployment_readiness_score || 0) - (a[1].deployment_readiness_score || 0));

    // Overfit risk: sort by avg_overfit_score desc (higher = worse)
    const overfitRanked = [...strategies]
        .map(s => [s, mergedRow(s)])
        .filter(([, d]) => d.avg_overfit_score != null)
        .sort((a, b) => (b[1].avg_overfit_score || 0) - (a[1].avg_overfit_score || 0));

    return (
        <div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
                <RankingsTable
                    title="Deployment Readiness"
                    rows={deployRanked}
                    valueKey="deployment_readiness_score"
                    invert={false}
                    thresholdWarn={0.45}
                    thresholdGood={0.65}
                />
                <RankingsTable
                    title="Operational Confidence"
                    rows={opConfRanked}
                    valueKey="operational_health_score"
                    invert={false}
                    thresholdWarn={0.45}
                    thresholdGood={0.65}
                />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                <RankingsTable
                    title="Regime Robustness"
                    rows={[...strategies]
                        .map(s => [s, mergedRow(s)])
                        .filter(([, d]) => d.avg_regime_robustness_score != null)
                        .sort((a, b) => (b[1].avg_regime_robustness_score || 0) - (a[1].avg_regime_robustness_score || 0))}
                    valueKey="avg_regime_robustness_score"
                    invert={false}
                    thresholdWarn={0.4}
                    thresholdGood={0.65}
                />
                <RankingsTable
                    title="Stability Score"
                    rows={stabilityRanked}
                    valueKey="avg_stability_score"
                    invert={false}
                    thresholdWarn={0.4}
                    thresholdGood={0.6}
                />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginTop: 16 }}>
                <RankingsTable
                    title="Overfit Risk (lower = better)"
                    rows={overfitRanked}
                    valueKey="avg_overfit_score"
                    invert={true}
                    thresholdWarn={0.2}
                    thresholdGood={0.1}
                />
            </div>
        </div>
    );
}
